import { addBusinessDays, addDays, differenceInBusinessDays } from 'date-fns';
import { DateTime } from 'luxon';

import CollectionNoteIcon from 'assets/dialogue-avatar-collection-note.svg';
import {
  extLnkCheckID,
  extLnkCollectionNomination,
  extLnkCollectMissedDelivery,
  extLnkPostOfficeLocatorWithShowpop,
} from 'config/track-config';
import { DialogueCardData, DialogueType } from 'models/DialogCard';
import {
  AddressType,
  ArticleAddress,
  ArticleEvent,
  CollectionInstruction,
  CollectionType,
  Milestone,
  RedirectStatus,
  ServiceCode,
} from 'models/shipments';
import { formatJSDate, localJSDateFromISO } from 'utils/date.utils';

import ViewAttemptedDeliveryCta from '../../../common/ViewAttemptedDeliveryCta/ViewAttemptedDeliveryCta';
import { ArrowLink } from '../Arrows';
import AccessCodeCTA from './AccessCodeCTA/AccessCodeCTA';
import ParcelCollectCTA from './ParcelCollectCTA/ParcelCollectCTA';

const awaitingCollectionDefaults = {
  type: DialogueType.COLLECTION_NOTE,
  icon: <CollectionNoteIcon />,
};

const parcelLockerCard = (
  date: string,
  workCentreId: string | null,
  attDelPhoto: boolean,
  accessCode: string,
  accessNumber?: string | null,
  buildingAccessInstruction?: string | null,
  mobileNumber?: string | null
) => ({
  ...awaitingCollectionDefaults,
  analyticsId: 'awaitingCollectionParcelLocker',
  title: 'It’s in your Parcel Locker',
  icon: <CollectionNoteIcon />,
  content: (
    <>
      <p>You can collect it any time before {date}.</p>
      <p>After that time, we’ll transfer it to a nearby Post Office.</p>
    </>
  ),
  customContent: (
    <>
      <AccessCodeCTA
        accessCode={accessCode}
        accessNumber={accessNumber}
        buildingAccessInstruction={buildingAccessInstruction}
        mobileNumber={mobileNumber}
      />
      {attDelPhoto ? <ViewAttemptedDeliveryCta /> : null}
    </>
  ),
  actions: <ArrowLink href={extLnkPostOfficeLocatorWithShowpop + workCentreId}>View location</ArrowLink>,
});

const poBoxCard = (
  date: string,
  workCentreId: string,
  trackingId: string,
  allowDelegate: boolean,
  attDelPhoto: boolean,
  serviceCode?: string | null
) => {
  let content;
  switch (serviceCode) {
    case ServiceCode.S8:
      content = (
        <>
          <p>
            {`You can collect it any time before the end of ${date}. After that date, we’ll return it to the sender.`}
          </p>
          <p>
            {`We can only hand over this parcel to the person it’s addressed to. They’ll need to show valid ID and sign for the parcel.`}
          </p>
        </>
      );
      break;
    case ServiceCode.WINE:
      content = (
        <>
          <p>
            {`You can collect it any time before the end of ${date}. After that date, we’ll return it to the sender.`}
          </p>
          <p>
            {`We can only hand over this parcel to the person it’s addressed to. They’ll need to show valid ID proving they’re at least 18 years old and sign for the parcel.`}
          </p>
        </>
      );
      break;
    default:
      content = (
        <>
          <p>{`You can collect it any time before the end of ${date}.`}</p>
          <p>{`After that date, we’ll return it to the sender.`}</p>
        </>
      );
  }
  return {
    ...awaitingCollectionDefaults,
    analyticsId: 'awaitingCollectionPOBox',
    title: 'It’s ready for collection',
    customContent: (
      <>
        <ParcelCollectCTA trackingId={trackingId} />
        {attDelPhoto ? <ViewAttemptedDeliveryCta /> : null}
      </>
    ),
    content,
    actions: (
      <>
        <ArrowLink href={extLnkPostOfficeLocatorWithShowpop + workCentreId}>
          View location and collection hours
        </ArrowLink>
        <ArrowLink href={extLnkCheckID}>Check ID requirements</ArrowLink>
        {allowDelegate ? (
          <ArrowLink href={extLnkCollectionNomination(trackingId)}>Let someone else collect it</ArrowLink>
        ) : null}
      </>
    ),
  };
};

const anonPostOfficeCard = (
  date: string,
  workCentreId: string,
  trackingId: string,
  allowDelegate: boolean,
  attDelPhoto: boolean,
  serviceCode?: string | null
) => {
  let content;
  switch (serviceCode) {
    case ServiceCode.S8:
      content = (
        <>
          <p>You can collect it any time before the end of {date}.</p>
          <p>
            {`We can only hand over this parcel to the person it’s addressed to. They’ll need to show valid ID and sign for the parcel.`}
          </p>
        </>
      );
      break;
    case ServiceCode.WINE:
      content = (
        <>
          <p>You can collect it any time before the end of {date}.</p>
          <p>
            {`We can only hand over this parcel to the person it’s addressed to. They’ll need to show valid ID proving they’re at least 18 years old and sign for the parcel.`}
          </p>
        </>
      );
      break;
    default:
      content = (
        <>
          <p>You can collect it any time before the end of {date}.</p>
          <p>You’ll need to show valid ID.</p>
        </>
      );
  }
  return {
    ...awaitingCollectionDefaults,
    analyticsId: 'anonAwaitingCollectionPostOffice',
    title: 'It’s ready for collection',
    content,
    customContent: (
      <>
        <ParcelCollectCTA trackingId={trackingId} />
        {attDelPhoto ? <ViewAttemptedDeliveryCta /> : null}
      </>
    ),
    actions: (
      <>
        <ArrowLink href={extLnkPostOfficeLocatorWithShowpop + workCentreId}>
          View location and collection hours
        </ArrowLink>
        <ArrowLink href={extLnkCheckID}>Check ID requirements</ArrowLink>
        {allowDelegate ? (
          <ArrowLink href={extLnkCollectionNomination(trackingId)}>Let someone else collect it</ArrowLink>
        ) : null}
      </>
    ),
  };
};

const postOfficeCard = (
  date: string,
  workCentreId: string,
  trackingId: string,
  allowDelegate: boolean,
  attDelPhoto: boolean,
  serviceCode?: string | null
) => {
  let content;
  switch (serviceCode) {
    case ServiceCode.S8:
      content = (
        <>
          <p>{`You can collect it any time before the end of ${date}. We’ll ask you for proof of identity at the counter.`}</p>
          <p>{`We can only hand over this parcel to the person it’s addressed to. They’ll need to show valid ID and sign for the parcel.`}</p>
        </>
      );
      break;
    case ServiceCode.WINE:
      content = (
        <>
          <p>{`You can collect it any time before the end of ${date}. We’ll ask you for proof of identity at the counter.`}</p>
          <p>{`We can only hand over this parcel to the person it’s addressed to. They’ll need to show valid ID proving they’re at least 18 years old and sign for the parcel.`}</p>
        </>
      );
      break;
    default:
      content = (
        <>
          <p>{`You can collect it any time before the end of ${date}.`}</p>
          <p>{`Show the collection QR code at the counter along with your proof of identity.`}</p>
        </>
      );
  }

  return {
    ...awaitingCollectionDefaults,
    analyticsId: 'awaitingCollectionPostOffice',
    title: 'It’s at the Post Office',
    content,
    customContent: (
      <>
        <ParcelCollectCTA trackingId={trackingId} />
        {attDelPhoto ? <ViewAttemptedDeliveryCta /> : null}
      </>
    ),
    actions: (
      <>
        <ArrowLink href={extLnkPostOfficeLocatorWithShowpop + workCentreId}>
          View location and collection hours
        </ArrowLink>
        <ArrowLink href={extLnkCheckID}>Check ID requirements</ArrowLink>
        {allowDelegate ? (
          <ArrowLink href={extLnkCollectionNomination(trackingId)}>Let someone else collect it</ArrowLink>
        ) : null}
      </>
    ),
  };
};

const failedRedirectCard = (
  date: string,
  workCentreId: string,
  trackingId: string,
  allowDelegate: boolean,
  attDelPhoto: boolean
) => ({
  ...postOfficeCard(date, workCentreId, trackingId, allowDelegate, attDelPhoto),
  analyticsId: 'awaitingCollectionFailedRedirection',
  content: (
    <>
      <p>We couldn’t redirect this parcel, so we delivered it to the previous address instead.</p>
      <p>You can collect it any time before the end of {date}.</p>
      <p>Show the collection QR code at the counter along with your proof of identity.</p>
    </>
  ),
});

const collectionReminderCard = (
  date: string,
  workCentreId: string,
  trackingId: string,
  allowDelegate: boolean,
  attDelPhoto: boolean,
  serviceCode?: string | null
) => {
  let content;

  switch (serviceCode) {
    case ServiceCode.S8:
      content = (
        <>
          <p>{`After that date, we’ll return this parcel to the sender.`}</p>
          <p>{`We can only hand over this parcel to the person it’s addressed to. They’ll need to show valid ID and sign for the parcel.`}</p>
        </>
      );
      break;
    case ServiceCode.WINE:
      content = (
        <>
          <p>{`After that date, we’ll return this parcel to the sender.`}</p>
          <p>{`We can only hand over this parcel to the person it’s addressed to. They’ll need to show valid ID proving they’re at least 18 years old and sign for the parcel.`}</p>
        </>
      );
      break;
    default:
      content = (
        <>
          <p>{`After that date, we’ll return this parcel to the sender.`}</p>
          <p>{`Show the collection QR code at the counter along with your proof of identity.`}</p>
        </>
      );
  }

  return {
    ...awaitingCollectionDefaults,
    analyticsId: 'collectionReminder',
    title: `We’ll hold it for you until ${date}`,
    content: content,
    customContent: (
      <>
        <ParcelCollectCTA trackingId={trackingId} />
        {attDelPhoto ? <ViewAttemptedDeliveryCta /> : null}
      </>
    ),
    actions: (
      <>
        <ArrowLink href={extLnkPostOfficeLocatorWithShowpop + workCentreId}>
          View location and collection hours
        </ArrowLink>
        {allowDelegate ? (
          <ArrowLink href={extLnkCollectionNomination(trackingId)}>Let someone else collect it</ArrowLink>
        ) : null}
        <ArrowLink href={extLnkCollectMissedDelivery}>Collection options and info</ArrowLink>
      </>
    ),
  };
};

enum DayThresholds {
  returnFromPostOffice = 10,
  transferFromPoBox = 30,
  transferFromParcelLocker = 2,
  collectionReminder = 5,
}

/**
 * Handles 4 awaiting collection dialogue card scenarios
 * Display logic:
 *    Awaiting collection from Parcel Locker - 48 hrs (2 days) after scan event
 *    Awaiting collection from PO Box - 30 days after scan event
 *    Awaiting collection from Post Office - 1-5 business days after scan event
 *    Awaiting collection from Post Office(reminder) - 6-10 business days after scan event
 */
export function getAwaitingCollectionCard(
  isAuth: boolean,
  articleId: string,
  latestEvent: ArticleEvent | null,
  collectionInstruction: CollectionInstruction | null,
  accessCode: string | null,
  attemptedDeliveryPhotoAvailable: boolean,
  buildingAccessInstruction?: string | null,
  redirectStatus?: RedirectStatus | null,
  serviceCode?: string | null,
  address?: ArticleAddress | null
): DialogueCardData | null {
  if (!articleId || !latestEvent || !collectionInstruction) return null;

  const { workCentreId: wcid, type } = collectionInstruction.facility || {};
  const accessNumber = collectionInstruction.accessNumber || null;
  const serviceCodeRequiresID = !!serviceCode && [ServiceCode.WINE, ServiceCode.S8].includes(serviceCode);
  const allowDelegate = serviceCodeRequiresID ? false : collectionInstruction.delegate?.allowed || false;

  if (!wcid || !type) return null;

  const { milestone, localeDateTime } = latestEvent;
  if (milestone !== Milestone.AWAITING_COLLECTION || !localeDateTime) return null;

  const latestEventDate = localJSDateFromISO(localeDateTime);

  if (type.toUpperCase() === CollectionType.PARCEL_LOCKER) {
    if (accessCode) {
      // Only receivers (Known or authenticated) will have access code
      const transferDate = addBusinessDays(latestEventDate, DayThresholds.transferFromParcelLocker);
      const dateStr = formatJSDate(transferDate, 'EEEE d MMMM');
      const parcelLockerExpiry = DateTime.fromJSDate(transferDate);
      const timeStr = parcelLockerExpiry.toFormat('h.mma').toLocaleLowerCase();
      const mobileNumber = address?.mobile || null;

      return parcelLockerCard(
        `${timeStr} on ${dateStr}`,
        wcid,
        attemptedDeliveryPhotoAvailable,
        accessCode,
        accessNumber,
        buildingAccessInstruction,
        mobileNumber
      );
    } else {
      // Non receivers, show no dialogue card
      return null;
    }
  } else {
    // PO Box Auth/known - Only auth and known users get back non null from addressType
    const isPoBox =
      isAuth &&
      ((type === CollectionType.WORK_CENTRE && address?.addressType === AddressType.PO_BOX) ||
        type === CollectionType.PO_BOX);
    if (isPoBox) {
      const transferDate = addDays(latestEventDate, DayThresholds.transferFromPoBox);
      const dateStr = formatJSDate(transferDate, 'EEEE d MMMM');
      return poBoxCard(dateStr, wcid, articleId, allowDelegate, attemptedDeliveryPhotoAvailable, serviceCode);
    }

    const transferDate = addBusinessDays(latestEventDate, DayThresholds.returnFromPostOffice);
    const daysUntilTransfer = differenceInBusinessDays(transferDate, new Date());
    const dateStr = formatJSDate(transferDate, 'EEEE d MMMM');

    // Do not display dialogue card on day of transfer
    if (!daysUntilTransfer || daysUntilTransfer < 1) return null;

    // if(!isAuth && type.toUpperCase() === CollectionType.PO_BOX)
    //   return poBoxCard(dateStr, wcid, articleId, allowDelegate, attemptedDeliveryPhotoAvailable, serviceCode);

    // For both anon/non receiver PC and PO Box
    if (!isAuth)
      return anonPostOfficeCard(dateStr, wcid, articleId, allowDelegate, attemptedDeliveryPhotoAvailable, serviceCode);

    // Auth PC
    if (daysUntilTransfer <= DayThresholds.collectionReminder)
      return collectionReminderCard(
        dateStr,
        wcid,
        articleId,
        allowDelegate,
        attemptedDeliveryPhotoAvailable,
        serviceCode
      );

    if (redirectStatus === RedirectStatus.REDIRECT_FAILED)
      return failedRedirectCard(dateStr, wcid, articleId, allowDelegate, attemptedDeliveryPhotoAvailable);

    return postOfficeCard(dateStr, wcid, articleId, allowDelegate, attemptedDeliveryPhotoAvailable, serviceCode);
  }
}
